import ItemCollector from '../../../Filters/queries/ItemCollector';
import ByLegCollector from '../../FiltersAccordion/queries/ByLegCollector';
import joinItems from '../../../Filters/queries/joinItems';
import {
  PerformedSearchRequestContext,
  PerformedSearchRequestContextSegment,
} from 'clientState/providers/SearchRequestProvider';
import { Location, Section } from 'generated/graphql';

// This type is just so we can do the cast down below to ensure
// typescript that the array has only two items
type AirportsByLegsCollectorBuildResult = {
  id: number;
} & {
  isAbleToBeUnfiltered: boolean;
  departureAirports: (Location & {
    checked: boolean;
    disabled: boolean;
  })[];
  destinationAirports: (Location & {
    checked: boolean;
    disabled: boolean;
  })[];
};

const joinRoundTripAirports = ([outbound, inbound]: [
  AirportsByLegsCollectorBuildResult,
  AirportsByLegsCollectorBuildResult
]) => {
  const departureAirports = joinItems([
    ...outbound.departureAirports,
    ...inbound.destinationAirports,
  ]);
  const destinationAirports = joinItems([
    ...outbound.destinationAirports,
    ...inbound.departureAirports,
  ]);
  return [
    {
      ...outbound,
      departureAirports,
      destinationAirports,
    },
  ];
};

const addAbleToBeUnfilteredProp = <T extends { isAbleToBeUnfiltered: boolean }>(
  airports: T[]
): { isAbleToBeUnfiltered: boolean; airports: T[] } => ({
  airports,
  isAbleToBeUnfiltered: airports.some(
    ({ isAbleToBeUnfiltered }) => isAbleToBeUnfiltered
  ),
});

const AirportCollector = (
  { tripType, itinerary }: PerformedSearchRequestContext,
  isUnbundle = false
) => {
  const byLegCollector = ByLegCollector(
    AirportLegCollector,
    itinerary.segments
  );
  return {
    add(legIndex: number, sections) {
      byLegCollector.add(legIndex, sections);
    },
    build() {
      const airports = byLegCollector.build();
      if (tripType === 'ROUND_TRIP' && !isUnbundle) {
        return addAbleToBeUnfilteredProp(
          joinRoundTripAirports(
            airports as [
              AirportsByLegsCollectorBuildResult,
              AirportsByLegsCollectorBuildResult
            ]
          )
        );
      }
      return addAbleToBeUnfilteredProp(airports);
    },
  };
};

const isRequestedLocation = (locationRequest, { iata }: Location) =>
  locationRequest?.type !== 'AIRPORT' ||
  locationRequest?.iata === iata ||
  locationRequest?.iataCode === iata;

const AirportLegCollector = (
  legIndex: number,
  segments?: PerformedSearchRequestContextSegment[]
) => {
  const departureByLegCollector = ItemCollector<
    Location & { checked: boolean; disabled: boolean }
  >();
  const destinationByLegCollector = ItemCollector<
    Location & { checked: boolean; disabled: boolean }
  >();
  const segmentRequest = segments?.[legIndex];
  let isAbleToBeUnfiltered = false;
  return {
    add({ sections }: { sections: Section[] }) {
      const { departure } = sections[0];
      const { destination } = sections[sections.length - 1];
      const departureChecked = isRequestedLocation(
        segmentRequest?.departure,
        departure
      );
      const destinationChecked = isRequestedLocation(
        segmentRequest?.destination,
        destination
      );
      isAbleToBeUnfiltered =
        isAbleToBeUnfiltered || !departureChecked || !destinationChecked;
      departureByLegCollector.add({
        ...departure,
        checked: departureChecked,
        disabled: false,
      });
      destinationByLegCollector.add({
        ...destination,
        checked: destinationChecked,
        disabled: false,
      });
    },
    build() {
      const departureAirports = departureByLegCollector.build();
      const destinationAirports = destinationByLegCollector.build();

      const setDisabledIfSingle = (airports) =>
        airports.length === 1
          ? airports.map((a) => ({ ...a, disabled: true }))
          : airports;

      return {
        isAbleToBeUnfiltered,
        departureAirports: setDisabledIfSingle(departureAirports),
        destinationAirports: setDisabledIfSingle(destinationAirports),
      };
    },
  };
};

export default AirportCollector;
